import React from 'react';
import { Spin, Form, Input, ConfigProvider, Image, Modal } from 'antd';
import { CommentOutlined, SendOutlined, FileOutlined, CloseOutlined } from '@ant-design/icons';
import { Button } from '@/components/ui/Button';
import AppModal from '@/pages/inside/tenant/AppModal';
import { MessageCompose, ChatHeader } from '@/components/module/chats';
import { Container } from '@/components/ui/Container';
import { GlobalImagePreviewStyles } from '@/components/ui/GlobalImagePreviewStyles';
import { truncateFileName, initialsFromName } from '@hooks/projects/discussion/utils/discussionPanel.utils';
import { useDiscussionPanel } from '@hooks/projects/discussion/useDiscussionPanel';
import { DiscussionContentEditor, DiscussionPostChatRow } from './DiscussionPanel.parts';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    OutlineIconButtonWrap,
    ChatMessageList,
    PanelSectionLabel,
    CreateRoleModalBody,
    ThreadFeedAccentWrap,
    ChatFullHeightWrap,
    ChatLayout,
    ChatMessagePanel,
    ChatMessageListWrap,
    DetailPanelLoading,
    EmptyStateFill,
    AttachmentCardsRow,
    AttachmentThumbnailBox,
    AttachmentThumbnailImg,
    AttachmentThumbnailFileWrap,
    AttachmentThumbnailFileExt,
    AttachmentRemoveBtn,
} from '@/pages/inside/tenant/style';

/**
 * Reusable discussion panel: compose (rich text + link + file attachments) + list of posts.
 * Same API contract: createPost({ content, attachments?, files? }) with JSON or FormData.
 * Used by Project Discussion tab and Bug/Blocker discussion side panels.
 */
export default function DiscussionPanel({
    discussions = [],
    isLoading = false,
    createPost,
    createPostPending = false,
    editPost,
    editPostPending = false,
    deletePost,
    deletePostPending = false,
    sectionLabel = 'Add Comments',
    emptyIcon = <CommentOutlined />,
    emptyTitle = 'No discussion yet',
    emptyDesc = 'Post a message above to start the conversation.',
    listStyle,
    composeStyle,
    /** When provided, project collaborators (assigned users, max 100) are used for @-mention dropdown. */
    projectId,
    mentionUsers: mentionUsersProp = [],
    hashtagTags: hashtagTagsProp = [],
    /** 'relative' (default) or 'YYYY-MM-DD HH:mm */
    timeFormat = 'relative',
    /** Orange avatar + left accent on bubbles (legacy; discussion rows use chat-style layout). */
    feedAccent = false,
    /** When variant='chat': show the top ChatHeader (project name/avatar). */
    showHeader = true,
    /**
     * `stacked` — compose on top, list below (bug/blocker side panels).
     * `chat` — same shell as main Chat: header, scrollable message list, {@link MessageCompose} with toolbar at bottom.
     */
    variant = 'stacked',
    /**
     * Base z-index used when the panel is rendered inside a SidePanel.
     * This lets dropdowns/modals opened from the discussion render above the side panel.
     */
    zIndexBase,
}) {
    const vm = useDiscussionPanel({
        discussions,
        isLoading,
        createPost,
        createPostPending,
        editPost,
        editPostPending,
        deletePost,
        deletePostPending,
        projectId,
        timeFormat,
        feedAccent,
        variant,
        zIndexBase,
    });

    const rootShellProps = {
        $display: 'flex',
        $flexDirection: 'column',
        $flex: '1 1 auto',
        $minHeight: '0',
        $overflow: 'hidden',
    };

    const modals = (
        <>
            {/* Modals placed outside main UI containers to ensure topmost portal visibility */}
            <AppModal
                title="Add link"
                open={vm.linkModalOpen}
                onCancel={() => { vm.setLinkModalOpen(false); vm.linkModalCallbackRef.current = null; }}
                size="md"
                zIndex={vm.modalZIndex}
                footer={
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button $primary={false} $size="14px" onClick={() => { vm.setLinkModalOpen(false); vm.linkModalCallbackRef.current = null; }}>Cancel</Button>
                        <Button $primary $blackOnLight $size="14px" onClick={vm.handleLinkModalSubmit}>Add link</Button>
                    </div>
                }
            >
                <CreateRoleModalBody>
                    <Form form={vm.linkModalForm} layout="vertical">
                        <Form.Item name="url" label="URL" rules={[{ required: true, message: 'Enter URL' }]}>
                            <Input placeholder="https://example.com/doc.pdf" />
                        </Form.Item>
                        <Form.Item name="title" label="Title (optional)">
                            <Input placeholder="e.g. Design doc" />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
            </AppModal>

            <AppModal
                title={vm.attachmentPreview?.name ?? 'Attachment'}
                open={!!vm.attachmentPreview && vm.attachmentPreview?.type !== 'image'}
                onCancel={vm.handleCloseAttachmentPreview}
                footer={<Button type="primary" $size="14px" $padding="6px 14px" $hoverShadow={false} onClick={vm.handleCloseAttachmentPreview}>Close</Button>}
                size="md"
                zIndex={vm.modalZIndex}
                styles={{
                    content: { borderRadius: 12, overflow: 'hidden' },
                    header: { padding: '8px 16px', borderBottomWidth: 1 },
                    body: { padding: 0, background: 'var(--ant-color-fill-quaternary, #f5f5f5)' },
                    footer: { padding: '8px 16px' },
                }}
            >
                <div style={{ width: '100%', height: '100%', minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ant-color-fill-quaternary, #f5f5f5)' }}>
                    {vm.attachmentPreviewLoading && <Spin size="large" />}
                    {vm.attachmentPreview?.type === 'pdf' && (
                        <iframe src={vm.attachmentPreview.url} title={vm.attachmentPreview.name} style={{ width: '100%', height: '100%', border: 'none' }} />
                    )}
                </div>
            </AppModal>

            {vm.attachmentPreview?.type === 'image' ? (
                <Image
                    src={vm.attachmentPreview.url}
                    alt={vm.attachmentPreview.name ?? 'Image'}
                    style={{ display: 'none' }}
                    preview={{
                        visible: true,
                        onVisibleChange: (visible) => {
                            if (!visible) vm.handleCloseAttachmentPreview();
                        },
                    }}
                />
            ) : null}

            {/* Delete Confirmation Modal - Raw Modal for guaranteed topmost portal */}
            <Modal
                maskClosable={false}
                title="Delete message?"
                open={vm.deleteModalOpen}
                onCancel={() => vm.setDeleteModalOpen(false)}
                footer={
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button $primary={false} $size="14px" onClick={() => vm.setDeleteModalOpen(false)}>No</Button>
                        <Button $primary danger $size="14px" loading={vm.deletePostPending} onClick={vm.handleDeleteConfirm}>Yes, Delete</Button>
                    </div>
                }
                zIndex={30000}
                getContainer={() => document.body}
                width={400}
                centered
            >
                <div style={{ padding: '10px 0' }}>
                    Are you sure you want to delete this message? This action cannot be undone.
                </div>
            </Modal>
        </>
    );

    if (variant === 'chat') {
        return (
            <ConfigProvider theme={{ token: { zIndexPopupBase: vm.popupZIndexBase } }}>
                <ChatFullHeightWrap>
                    <GlobalImagePreviewStyles />
                    <ChatLayout>
                        <ChatMessagePanel>
                            {showHeader ? (
                                <ChatHeader
                                    projectName={vm.project?.name ?? 'Project'}
                                    projectStatus={vm.project?.status}
                                    participantName="Team discussion"
                                    participantEmail=""
                                    initials={initialsFromName(vm.project?.name ?? 'Discussion')}
                                    avatarColor={vm.discussionHeaderAvatar}
                                />
                            ) : null}
                            {vm.isLoading ? (
                                <Container $flex="1 1 auto" $display="flex" $alignItems="center" $justifyContent="center">
                                    <DetailPanelLoading>
                                        <Spin size="large" />
                                    </DetailPanelLoading>
                                </Container>
                            ) : (
                                <ChatMessageListWrap>
                                    <ChatMessageList ref={vm.messageListRef}>
                                        <Container $flex="1 1 auto" />
                                        {vm.discussionsForList.length === 0 ? (
                                            <EmptyStateFill>{emptyTitle}</EmptyStateFill>
                                        ) : (
                                            vm.discussionsForList.map((post) => (
                                                <DiscussionPostChatRow
                                                    key={post.id ?? post.discussionId}
                                                    post={post}
                                                    currentUserId={vm.currentUserId}
                                                    currentUserName={vm.currentUserName}
                                                    mentionUsers={vm.mentionUsers}
                                                    mentionMilestones={vm.mentionMilestones}
                                                    mentionSprints={vm.mentionSprints}
                                                    flatTasks={vm.flatTasks}
                                                    projectBugs={vm.projectBugs}
                                                    projectBlockers={vm.projectBlockers}
                                                    timeFormat={vm.timeFormat}
                                                    onEdit={() => vm.handleEditClick(post)}
                                                    onDelete={() => vm.confirmDelete(post.id ?? post.discussionId)}
                                                    editPending={vm.editPostPending}
                                                    deletePending={vm.deletePostPending}
                                                />
                                            ))
                                        )}
                                        <div ref={vm.scrollAnchorRef} />
                                    </ChatMessageList>
                                </ChatMessageListWrap>
                            )}

                            <Container $flex="0 0 auto" $padding="12px 16px" $borderTop="1px solid var(--ant-color-border, #f0f0f0)" $position="relative">
                                {vm.editingId && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            background: vm.dark ? 'rgba(168, 85, 247, 0.15)' : 'rgba(168, 85, 247, 0.08)',
                                            border: `1px solid ${vm.dark ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.2)'}`,
                                            marginBottom: 8,
                                            animation: 'fadeIn 0.2s ease-out',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                            <div
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    color: '#a855f7',
                                                    background: vm.dark ? 'rgba(168, 85, 247, 0.2)' : 'rgba(168, 85, 247, 0.1)',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                Editing
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: vm.dark ? 'rgba(255,255,255,0.85)' : '#444',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    opacity: 0.9,
                                                }}
                                            >
                                                {vm.editingOriginalText || '—'}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={vm.cancelEdit}
                                            title="Cancel Edit"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 22,
                                                height: 22,
                                                borderRadius: '50%',
                                                border: 'none',
                                                background: vm.dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                                color: vm.dark ? 'rgba(255,255,255,0.45)' : '#999',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                                                e.currentTarget.style.color = '#ef4444';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = vm.dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                                                e.currentTarget.style.color = vm.dark ? 'rgba(255,255,255,0.45)' : '#999';
                                            }}
                                        >
                                            <CloseOutlined style={{ fontSize: 10 }} />
                                        </button>
                                    </div>
                                )}
                                <MessageCompose
                                    ref={vm.discussionComposeRef}
                                    onSend={vm.handleChatSend}
                                    placeholder="Message project discussion…"
                                    disabled={createPostPending}
                                    popupZIndex={vm.popupZIndexBase}
                                    mentionUsers={vm.mentionUsers}
                                    mentionMilestones={vm.mentionMilestones}
                                    mentionSprints={vm.mentionSprints}
                                    hashtagTags={vm.publicTags}
                                    initialFiles={vm.initialFiles}
                                />
                            </Container>
                        </ChatMessagePanel>
                    </ChatLayout>
                </ChatFullHeightWrap>
                {modals}
            </ConfigProvider>
        );
    }

    return (
        <ConfigProvider theme={{ token: { zIndexPopupBase: vm.popupZIndexBase } }}>
            <div style={{ display: 'contents' }}>
                <GlobalImagePreviewStyles />
                {feedAccent ? (
                    <ThreadFeedAccentWrap>
                        <Container {...rootShellProps}>
                            <Container $flex="0 0 auto" $paddingBottom="16px" $borderBottom="1px solid var(--ant-color-border, #f0f0f0)" $position="relative">
                                <Container $marginBottom="8px">
                                    <PanelSectionLabel>{sectionLabel}</PanelSectionLabel>
                                </Container>
                                {vm.editingId && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            padding: '6px 12px',
                                            borderRadius: '8px',
                                            background: vm.dark ? 'rgba(168, 85, 247, 0.15)' : 'rgba(168, 85, 247, 0.08)',
                                            border: `1px solid ${vm.dark ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.2)'}`,
                                            marginBottom: 12,
                                            animation: 'fadeIn 0.2s ease-out',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                            <div
                                                style={{
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    color: '#a855f7',
                                                    background: vm.dark ? 'rgba(168, 85, 247, 0.2)' : 'rgba(168, 85, 247, 0.1)',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                Editing
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: vm.dark ? 'rgba(255,255,255,0.85)' : '#444',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    opacity: 0.9,
                                                }}
                                            >
                                                {vm.editingOriginalText || '—'}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={vm.cancelEdit}
                                            title="Cancel Edit"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 22,
                                                height: 22,
                                                borderRadius: '50%',
                                                border: 'none',
                                                background: vm.dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                                color: vm.dark ? 'rgba(255,255,255,0.45)' : '#999',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                                                e.currentTarget.style.color = '#ef4444';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = vm.dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                                                e.currentTarget.style.color = vm.dark ? 'rgba(255,255,255,0.45)' : '#999';
                                            }}
                                        >
                                            <CloseOutlined style={{ fontSize: 10 }} />
                                        </button>
                                    </div>
                                )}
                                <Form form={vm.form} layout="vertical">
                                    <Form.Item name="content" label={null} style={{ marginBottom: 0 }}>
                                        <DiscussionContentEditor
                                            mentionUsers={vm.mentionUsers}
                                            mentionMilestones={vm.mentionMilestones}
                                            mentionSprints={vm.mentionSprints}
                                            hashtagTags={vm.publicTags}
                                            onFilesSelected={vm.handlePostFilesSelected}
                                            onLinkRequest={vm.handleLinkRequest}
                                            onAttachmentRemoved={vm.removePendingPostAttachmentByIndex}
                                            onAttachmentLinkClick={(href, name) => vm.handleAttachmentClick({ href, url: href, name })}
                                        />
                                    </Form.Item>
                                </Form>

                                {vm.pendingPostAttachments.length > 0 && (
                                    <AttachmentCardsRow style={{ paddingTop: 8, borderTop: '1px solid var(--ant-color-border, #f0f0f0)' }}>
                                        {vm.pendingPostAttachments.map((p) => {
                                            const isImg = (p.type || '').startsWith('image/');
                                            return (
                                                <AttachmentThumbnailBox key={p.id}>
                                                    {isImg && (p.dataUrl || p.url) ? (
                                                        <AttachmentThumbnailImg src={p.dataUrl || p.url} alt={p.name} />
                                                    ) : (
                                                        <AttachmentThumbnailFileWrap>
                                                            <FileOutlined style={{ color: 'var(--ant-color-text-secondary)' }} />
                                                            <AttachmentThumbnailFileExt title={p.name}>
                                                                {truncateFileName(p.name)}
                                                            </AttachmentThumbnailFileExt>
                                                        </AttachmentThumbnailFileWrap>
                                                    )}
                                                    <AttachmentRemoveBtn type="button" aria-label="Remove attachment" onClick={() => vm.removePendingPostAttachment(p.id)}>
                                                        <CloseOutlined />
                                                    </AttachmentRemoveBtn>
                                                </AttachmentThumbnailBox>
                                            );
                                        })}
                                    </AttachmentCardsRow>
                                )}

                                <Container as={OutlineIconButtonWrap} $position="absolute" $right="12px" $bottom="14px">
                                    <Button
                                        type="button"
                                        $primary={false}
                                        icon={<SendOutlined />}
                                        loading={createPostPending}
                                        onClick={vm.handleSubmit}
                                        title="Send"
                                        shape="circle"
                                        size="large"
                                        $padding="0"
                                        $size="16px"
                                        $hoverShadow={false}
                                        $entryAnimation={false}
                                    />
                                </Container>
                            </Container>

                            <ChatMessageList $flex="1 1 auto">
                                {vm.isLoading ? (
                                    <EmptyStateBlock>
                                        <Spin size="large" />
                                    </EmptyStateBlock>
                                ) : vm.discussionsForList.length === 0 ? (
                                    <EmptyStateBlock>
                                        <EmptyStateIcon>{emptyIcon}</EmptyStateIcon>
                                        <EmptyStateTitle>{emptyTitle}</EmptyStateTitle>
                                        <EmptyStateDescription>{emptyDesc}</EmptyStateDescription>
                                    </EmptyStateBlock>
                                ) : (
                                    vm.discussionsForList.map((post) => (
                                        <DiscussionPostChatRow
                                            key={post.id ?? post.discussionId}
                                            post={post}
                                            currentUserId={vm.currentUserId}
                                            currentUserName={vm.currentUserName}
                                            mentionUsers={vm.mentionUsers}
                                            mentionMilestones={vm.mentionMilestones}
                                            mentionSprints={vm.mentionSprints}
                                            flatTasks={vm.flatTasks}
                                            projectBugs={vm.projectBugs}
                                            projectBlockers={vm.projectBlockers}
                                            timeFormat={vm.timeFormat}
                                            onEdit={() => vm.handleEditClick(post)}
                                            onDelete={() => vm.confirmDelete(post.id ?? post.discussionId)}
                                            editPending={vm.editPostPending}
                                            deletePending={vm.deletePostPending}
                                        />
                                    ))
                                )}
                            </ChatMessageList>
                        </Container>
                    </ThreadFeedAccentWrap>
                ) : (
                    <Container {...rootShellProps}>
                        <Container $flex="0 0 auto" $paddingBottom="16px" $borderBottom="1px solid var(--ant-color-border, #f0f0f0)" $position="relative">
                            <Container $marginBottom="8px">
                                <PanelSectionLabel>{sectionLabel}</PanelSectionLabel>
                            </Container>
                            {vm.editingId && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 12,
                                        padding: '6px 12px',
                                        borderRadius: '8px',
                                        background: vm.dark ? 'rgba(168, 85, 247, 0.15)' : 'rgba(168, 85, 247, 0.08)',
                                        border: `1px solid ${vm.dark ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.2)'}`,
                                        marginBottom: 8,
                                        animation: 'fadeIn 0.2s ease-out',
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                                        <div
                                            style={{
                                                fontSize: 10,
                                                fontWeight: 800,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                color: '#a855f7',
                                                background: vm.dark ? 'rgba(168, 85, 247, 0.2)' : 'rgba(168, 85, 247, 0.1)',
                                                padding: '2px 6px',
                                                borderRadius: '4px',
                                                flexShrink: 0,
                                            }}
                                        >
                                            Editing
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: vm.dark ? 'rgba(255,255,255,0.85)' : '#444',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                opacity: 0.9,
                                            }}
                                        >
                                            {vm.editingOriginalText || '—'}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={vm.cancelEdit}
                                        title="Cancel Edit"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 22,
                                            height: 22,
                                            borderRadius: '50%',
                                            border: 'none',
                                            background: vm.dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                                            color: vm.dark ? 'rgba(255,255,255,0.45)' : '#999',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)';
                                            e.currentTarget.style.color = '#ef4444';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = vm.dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
                                            e.currentTarget.style.color = vm.dark ? 'rgba(255,255,255,0.45)' : '#999';
                                        }}
                                    >
                                        <CloseOutlined style={{ fontSize: 10 }} />
                                    </button>
                                </div>
                            )}
                            <Form form={vm.form} layout="vertical">
                                <Form.Item name="content" label={null} style={{ marginBottom: 0 }}>
                                    <DiscussionContentEditor
                                        mentionUsers={vm.mentionUsers}
                                        mentionMilestones={vm.mentionMilestones}
                                        mentionSprints={vm.mentionSprints}
                                        hashtagTags={vm.publicTags}
                                        onFilesSelected={vm.handlePostFilesSelected}
                                        onLinkRequest={vm.handleLinkRequest}
                                        onAttachmentRemoved={vm.removePendingPostAttachmentByIndex}
                                        onAttachmentLinkClick={(href, name) => vm.handleAttachmentClick({ href, url: href, name })}
                                    />
                                </Form.Item>
                            </Form>
                            {vm.pendingPostAttachments.length > 0 && (
                                <AttachmentCardsRow style={{ paddingTop: 8, borderTop: '1px solid var(--ant-color-border, #f0f0f0)' }}>
                                    {vm.pendingPostAttachments.map((p) => {
                                        const isImg = (p.type || '').startsWith('image/');
                                        return (
                                            <AttachmentThumbnailBox key={p.id}>
                                                {(isImg && (p.dataUrl || p.url)) ? (
                                                    <AttachmentThumbnailImg src={p.dataUrl || p.url} alt={p.name} />
                                                ) : (
                                                    <AttachmentThumbnailFileWrap>
                                                        <FileOutlined style={{ color: 'var(--ant-color-text-secondary)' }} />
                                                        <AttachmentThumbnailFileExt title={p.name}>
                                                            {truncateFileName(p.name)}
                                                        </AttachmentThumbnailFileExt>
                                                    </AttachmentThumbnailFileWrap>
                                                )}
                                                <AttachmentRemoveBtn type="button" aria-label="Remove attachment" onClick={() => vm.removePendingPostAttachment(p.id)}>
                                                    <CloseOutlined />
                                                </AttachmentRemoveBtn>
                                            </AttachmentThumbnailBox>
                                        );
                                    })}
                                </AttachmentCardsRow>
                            )}
                            <Container as={OutlineIconButtonWrap} $position="absolute" $right="12px" $bottom="14px">
                                <Button
                                    type="button"
                                    $primary={false}
                                    icon={<SendOutlined />}
                                    loading={createPostPending}
                                    onClick={vm.handleSubmit}
                                    title="Send"
                                    shape="circle"
                                    size="large"
                                    $padding="0"
                                    $size="16px"
                                    $hoverShadow={false}
                                    $entryAnimation={false}
                                />
                            </Container>
                        </Container>

                        <ChatMessageList>
                            {vm.isLoading ? (
                                <EmptyStateBlock>
                                    <Spin size="large" />
                                </EmptyStateBlock>
                            ) : vm.discussionsForList.length === 0 ? (
                                <EmptyStateBlock>
                                    <EmptyStateIcon>{emptyIcon}</EmptyStateIcon>
                                    <EmptyStateTitle>{emptyTitle}</EmptyStateTitle>
                                    <EmptyStateDescription>{emptyDesc}</EmptyStateDescription>
                                </EmptyStateBlock>
                            ) : (
                                vm.discussionsForList.map((post) => (
                                    <DiscussionPostChatRow
                                        key={post.id ?? post.discussionId}
                                        post={post}
                                        currentUserId={vm.currentUserId}
                                        currentUserName={vm.currentUserName}
                                        mentionUsers={vm.mentionUsers}
                                        mentionMilestones={vm.mentionMilestones}
                                        mentionSprints={vm.mentionSprints}
                                        flatTasks={vm.flatTasks}
                                        projectBugs={vm.projectBugs}
                                        projectBlockers={vm.projectBlockers}
                                        timeFormat={vm.timeFormat}
                                        onEdit={() => vm.handleEditClick(post)}
                                        onDelete={() => vm.confirmDelete(post.id ?? post.discussionId)}
                                        editPending={vm.editPostPending}
                                        deletePending={vm.deletePostPending}
                                    />
                                ))
                            )}
                        </ChatMessageList>
                    </Container>
                )}
                {modals}
            </div>
        </ConfigProvider>
    );
}
